import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useRoom } from "./MatchRoom";
import { playersOf } from "../../lib/useMatch";
import { api, errMsg, label } from "../../lib/api";
import { MatchHeader } from "../../components/MatchHeader";
import { EventTimeline } from "../../components/EventTimeline";
import { CardAlert } from "../../components/Device";
import { ClockDialog, TextDialog, PlayerPicker } from "../../components/RefereeKit";
import { Panel, Spinner, Empty, Avatar } from "../../components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const BTN = [
  ["goal", "Goal", "bg-[#17191C] text-white"], ["foul", "Foul", "bg-[#343A40] text-white"], ["yellow", "Yellow Card", "bg-[#FFD400] text-[#17191C]"],
  ["red", "Red Card", "bg-[#F02030] text-white"], ["substitution", "Substitution", "bg-[#E6E9ED] text-[#17191C]"], ["time", "Match Time", "bg-white border-2 border-[#17191C] text-[#17191C]"],
  ["penalty", "Penalty", "bg-[#E6E9ED] text-[#17191C]"], ["own_goal", "Own Goal", "bg-[#E6E9ED] text-[#17191C]"], ["added", "Added Time", "bg-white border-2 border-[#C5CBD1] text-[#17191C]"],
  ["suspend", "Suspend Match", "bg-white border-2 border-[#F02030] text-[#F02030]"], ["notes", "Notes", "bg-white border-2 border-[#C5CBD1] text-[#17191C]"], ["var", "Video Review", "bg-white border-2 border-[#C5CBD1] text-[#17191C]"],
];

export function Correction({ ev, onClose, mid, reload }) {
  const [reason, setReason] = useState("");
  const [minute, setMinute] = useState(ev ? ev.minute + 1 : 0);
  useEffect(() => { if (ev) { setMinute(ev.minute + 1); setReason(""); } }, [ev]);
  const send = async (action) => {
    try {
      await api.post(`/matches/${mid}/events/${ev.id}/correct`, { action, reason, minute: action === "amend" ? minute - 1 : undefined });
      toast.success("Correction recorded in audit history");
      reload();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!ev} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="correction-dialog">
        <DialogHeader><DialogTitle>Correct event · {label(ev?.type)}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6C757D]">Official events cannot be deleted. Corrections are permanently recorded with your reason.</p>
        <Input data-testid="correction-reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex items-center gap-2"><span className="tro-label">Minute</span><Input type="number" data-testid="correction-minute" className="w-24" value={minute} onChange={(e) => setMinute(+e.target.value)} />
          <Button variant="outline" disabled={reason.length < 3} data-testid="correction-amend-btn" onClick={() => send("amend")}>Amend minute</Button></div>
        <Button disabled={reason.length < 3} data-testid="correction-void-btn" className="bg-[#F02030] hover:bg-[#d81a29]" onClick={() => send("void")}>Void event</Button>
        {ev?.corrections?.length > 0 && <div className="text-xs text-[#6C757D]">History: {ev.corrections.map((c) => `${c.action} (${c.reason})`).join("; ")}</div>}
      </DialogContent>
    </Dialog>
  );
}

export default function MatchControls() {
  const { id } = useParams();
  const R = useRoom(id);
  const m = R.match;
  const [flow, setFlow] = useState(null);
  const [penKind, setPenKind] = useState("penalty_awarded");
  const [subOut, setSubOut] = useState(null);
  const [corr, setCorr] = useState(null);
  const [added, setAdded] = useState(false);
  const [mins, setMins] = useState(3);
  if (!m) return <Spinner />;
  if (!m.my_official_roles?.includes("referee")) return <Empty text="Only the appointed referee can use Match Controls." />;
  const teams = [m.home, m.away].map((s) => {
    const p = playersOf(m, s.team_id);
    return { id: s.team_id, name: s.name, players: p.onField, bench: p.bench, all: p.all || [] };
  });
  const cardTeams = teams.map((t) => ({ ...t, players: t.all.filter((p) => !m.player_state?.[p.id]?.red) }));
  const press = async (k) => {
    if (["goal", "foul", "own_goal", "substitution", "penalty"].includes(k)) setFlow(k);
    else if (["yellow", "red"].includes(k)) setFlow(k);
    else if (k === "time") setFlow("time");
    else if (k === "added") setAdded(true);
    else if (k === "suspend") {
      try { await R.clockAction(m.status === "suspended" ? "resume_match" : "suspend"); toast.success(m.status === "suspended" ? "Match resumed" : "Match suspended"); } catch (e) { toast.error(errMsg(e)); }
    } else setFlow(k);
  };
  const pick = async (p, t) => {
    try {
      if (flow === "substitution") return setSubOut({ p, t });
      const type = flow === "penalty" ? penKind : flow;
      await R.recordCard(type, t.id, p, {});
      setFlow(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="match-controls-screen">
      <div className="flex items-center gap-2"><Link to={`/referee/matches/${id}/tactical`} data-testid="back-to-tactical" className="flex min-h-[44px] items-center gap-2 rounded-lg border border-[#C5CBD1] px-3 text-sm font-bold hover:bg-[#F7F8F9]"><ArrowLeft className="h-4 w-4" />Tactical screen</Link></div>
      <MatchHeader match={m} elapsed={R.elapsed} conn={R.conn} queue={R.queue} compact />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="controls-grid">
        {BTN.map(([k, l, c]) => (
          <button key={k} data-testid={`control-${k}`} onClick={() => press(k)} className={`min-h-[84px] rounded-2xl text-lg font-extrabold uppercase tracking-wide shadow-sm transition-transform active:scale-[0.97] md:min-h-[104px] md:text-xl ${c}`}>
            {k === "suspend" && m.status === "suspended" ? "Resume Match" : l}
          </button>
        ))}
      </div>
      <Panel title="Live event timeline"><EventTimeline events={R.events} match={m} onCorrect={setCorr} /></Panel>
      <PlayerPicker open={["goal", "foul", "own_goal", "yellow", "red", "substitution", "penalty"].includes(flow)} title={flow === "penalty" ? "Penalty" : label(flow)}
        teams={["yellow", "red"].includes(flow) ? cardTeams : teams} onClose={() => setFlow(null)} onPick={pick} />
      {flow === "penalty" && (
        <div className="fixed inset-x-0 top-4 z-[60] mx-auto flex w-fit gap-2 rounded-xl bg-white p-2 shadow-lg" data-testid="penalty-kind">
          {["penalty_awarded", "penalty_scored", "penalty_missed"].map((k) => <button key={k} data-testid={`pen-${k}`} onClick={() => setPenKind(k)} className={`rounded-lg px-3 py-2 text-xs font-bold ${penKind === k ? "bg-[#17191C] text-white" : "bg-[#F7F8F9]"}`}>{label(k)}</button>)}
        </div>
      )}
      <Dialog open={!!subOut} onOpenChange={(o) => !o && setSubOut(null)}>
        <DialogContent data-testid="sub-in-dialog"><DialogHeader><DialogTitle>Replace #{subOut?.p.number} {subOut?.p.short_name} with</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">{subOut?.t.bench.map((b) => (
            <button key={b.id} data-testid={`controls-sub-in-${b.number}`} onClick={async () => { try { await R.recordCard("substitution", subOut.t.id, subOut.p, { player_in_id: b.id }); setSubOut(null); setFlow(null); } catch (e) { toast.error(errMsg(e)); } }}
              className="flex items-center gap-2 rounded-lg border p-2 hover:bg-[#F7F8F9]"><Avatar player={b} size={36} /><b className="text-sm">#{b.number} {b.short_name}</b></button>
          ))}</div>
        </DialogContent>
      </Dialog>
      <ClockDialog open={flow === "time"} onClose={() => setFlow(null)} match={m} onAction={R.clockAction} />
      <TextDialog open={flow === "notes"} title="Match note" placeholder="Official note" testid="controls-note-dialog" onClose={() => setFlow(null)} onSubmit={(t) => R.record({ type: "note", notes: t })} />
      <TextDialog open={flow === "var"} title="Video review request (advisory record)" placeholder="Describe the incident for review" testid="var-dialog" onClose={() => setFlow(null)} onSubmit={(t) => R.record({ type: "var_review", notes: t })} />
      <Dialog open={added} onOpenChange={setAdded}>
        <DialogContent data-testid="added-time-dialog"><DialogHeader><DialogTitle>Added time for {label(m.status)}</DialogTitle></DialogHeader>
          <div className="flex gap-2">{[1, 2, 3, 4, 5, 6, 8, 10].map((n) => <button key={n} onClick={() => setMins(n)} className={`h-14 flex-1 rounded-lg font-mono text-lg font-bold ${mins === n ? "bg-[#17191C] text-white" : "bg-[#F7F8F9]"}`}>{n}</button>)}</div>
          <Button data-testid="added-time-confirm" className="h-12 font-extrabold" onClick={async () => { try { await R.clockAction("add_time", { minutes: mins }); setAdded(false); toast.success(`+${mins}' added`); } catch (e) { toast.error(errMsg(e)); } }}>Confirm +{mins}'</Button>
        </DialogContent>
      </Dialog>
      <Correction ev={corr} onClose={() => setCorr(null)} mid={id} reload={R.load} />
      <CardAlert alert={R.alert} onClose={() => R.setAlert(null)} />
    </div>
  );
}
